import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import SeoPageShell from "@/components/SeoPageShell";
import { trackPublicEvent } from "@/lib/publicAnalytics";
import aboutMaurizioPortrait from "@/assets/about-maurizio-portrait.webp";

const source = "about";

function track(section, ctaId) {
  trackPublicEvent("cta_click", { source, section, cta_id: ctaId });
}

export default function AboutPage() {
  return (
    <SeoPageShell source={source}>
      <main>
        <section className="seo-marketing-hero product-feature-hero about-page-hero px-5 pb-12 pt-12 sm:px-8 sm:pb-16 sm:pt-20">
            <div className="relative mx-auto max-w-7xl">
            <div className="about-page-hero__inner mx-auto max-w-4xl text-center">
              <div className="landing-anti-summary-pill mb-6 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs uppercase tracking-[0.2em] marketing-text-secondary">
                <span>About Callsect</span>
              </div>
              <h1 className="product-feature-hero__title about-page-hero__title">Why I built Callsect</h1>
              <div className="about-page-hero__copy marketing-text-secondary">
                <p>
                  I spend a lot of my working day talking to customers. What kept bothering me wasn’t getting a transcript or another summary. It was everything that happened between conversations: remembering what changed, what someone had promised, where the relationship was heading, and what I needed to do next.
                </p>
                <p>Callsect started from that problem.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="about-editorial px-5 py-12 sm:px-8 sm:py-16">
          <div className="mx-auto max-w-4xl">
            <article className="about-editorial__section">
              <h2>Built in the workflow</h2>
              <p>
                I use Callsect myself in everyday account-management work. That means I’m not testing it only against demo scenarios. When something creates friction during a real renewal, follow-up, handoff, or customer conversation, I run into that friction too.
              </p>
              <p>
                A lot of the product has evolved that way: something is annoying in the actual workflow, I try to understand why, and Callsect gets adjusted around the problem.
              </p>
            </article>

            <article className="about-editorial__section">
              <h2>Born in account management</h2>
              <p>
                I originally built Callsect around problems I kept running into in account management. The same problems show up in Sales, Customer Success, Renewals, and other customer-facing work where the same relationships continue across multiple conversations.
              </p>
            </article>

            <article className="about-editorial__section">
              <h2>What I’m trying to build</h2>
              <p>
                I don’t want Callsect to become another tool that creates more work around the conversation. The goal is simpler: after a call, help me understand what mattered, remember what should carry forward, and make the next conversation easier to handle.
              </p>
              <p>
                That is still the standard I use when deciding what belongs in the product.
              </p>
            </article>
          </div>
        </section>

        <section className="about-founder px-5 pb-12 sm:px-8 sm:pb-16" aria-labelledby="about-founder-name">
          <div className="about-founder__inner mx-auto max-w-4xl">
            <figure className="about-founder__figure">
              <img
                className="about-founder__portrait"
                src={aboutMaurizioPortrait}
                alt=""
                loading="lazy"
                decoding="async"
              />
              <figcaption className="about-founder__caption">
                <h2 id="about-founder-name">Maurizio Ciocca</h2>
                <p className="about-founder__role">Account Manager · Builder of Callsect</p>
                <p className="about-founder__note">
                  I’m an account manager by day and I built Callsect because I wanted this tool for my own calls first.
                </p>
              </figcaption>
            </figure>
          </div>
        </section>

        <section className="about-compact-cta px-5 pb-16 sm:px-8 sm:pb-20">
          <div className="about-compact-cta__inner mx-auto max-w-4xl">
            <div>
              <h2>Want to see what that became?</h2>
              <p>Try the interactive demo, or sign up free and use Callsect with your own conversations.</p>
            </div>
            <div className="about-compact-cta__actions">
              <Link
                to="/login?mode=register"
                onClick={() => track("final_cta", "sign_up_free")}
                className="product-feature-final__primary"
              >
                Sign up free
              </Link>
              <Link
                to="/demo"
                onClick={() => track("final_cta", "see_demo")}
                className="product-feature-final__secondary"
              >
                See Demo
                <ArrowRight size={17} weight="bold" />
              </Link>
            </div>
          </div>
        </section>
      </main>
    </SeoPageShell>
  );
}
